import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye, CheckCircle, Clock, Calendar, XCircle, AlertCircle, ChevronUp, Tag, ExternalLink,
  Briefcase, FileText, TrendingUp, Archive, Languages, MessageSquare, ClipboardList, UserRound
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tooltip, CircularProgress } from '@mui/material';
import { getTagColor } from '../../../utils/tagColors';
import TagEditor from '../pages/candidate/TagEditor';
import { getInterviewRowId } from './interviewUtils';
import useTableVirtualizer from './useTableVirtualizer';

// Sortable Header Component (uses native title tooltip only — no expansion on hover)
const SortableHeader = React.memo(({ children, label, sortKey, currentSortKey, sortDirection, onSort }) => {
  const isActive = currentSortKey === sortKey;

  // derive a string label for title fallback when children is simple text
  let labelText = label;
  if (!labelText) {
    if (typeof children === 'string') labelText = children;
    else if (Array.isArray(children)) labelText = children.map(c => (typeof c === 'string' ? c : '')).join('');
    else labelText = undefined;
  }

  return (
    <th
      style={{ overflow: 'hidden' }}
      className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none align-middle"
      onClick={() => onSort(sortKey)}
      title={labelText}
      aria-label={labelText}
    >
      <div className="flex items-center gap-1">
        <span className="truncate" style={{ maxWidth: '100%' }}>
          {children}
        </span>

        {isActive ? (
          sortDirection === 'asc' ? (
            <ChevronUp size={13} className="text-blue-600" />
          ) : (
            <ChevronUp size={13} className="text-blue-600 rotate-180" />
          )
        ) : (
          <ChevronUp size={13} className="opacity-30" />
        )}
      </div>
    </th>
  );
});

const adminInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const truncateWithTooltip = (text, maxLength = 20) => {
  if (!text) return { display: '', tooltip: '' };
  const stringText = String(text);
  if (stringText.length > maxLength) {
    return { display: `${stringText.substring(0, maxLength)}...`, tooltip: stringText };
  }
  return { display: stringText, tooltip: stringText };
};

const CompanyAdminName = React.memo(({ name }) => {
  if (!name) return <span className="text-sm text-gray-400">—</span>;
  const { display, tooltip } = truncateWithTooltip(name, 25);
  return (
    <div className="inline-flex items-center gap-2 min-w-0" title={tooltip}>
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-semibold tracking-wide text-white">
        {adminInitials(name)}
      </span>
      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{display}</span>
    </div>
  );
});

const hoverTipSlotProps = {
  popper: {
    modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
  },
  tooltip: {
    sx: {
      background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.01em',
      borderRadius: '8px',
      px: 1.25,
      py: 0.6,
      boxShadow: '0 8px 20px rgba(14, 165, 233, 0.28)',
      maxWidth: 280,
    },
  },
};

const HoverTip = ({ title, children }) => {
  if (!title) return children;
  return (
    <Tooltip title={title} placement="top" enterDelay={200} slotProps={hoverTipSlotProps}>
      {children}
    </Tooltip>
  );
};

const formatScore = (score) => (score === null || score === undefined ? '0.00%' : `${score.toFixed(2)}%`);

const getCompanyId = (candidate = {}) => {
  const companyId = candidate.company_id;
  if (companyId) return companyId;

  try {
    const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
    if (adminToken) {
      const tokenPayload = JSON.parse(atob(adminToken.split('.')[1]));
      return tokenPayload.company_id;
    }
  } catch (err) { }

  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('adminUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.admin_companies && user.admin_companies.length > 0) {
        return user.admin_companies[0];
      }
    }
  } catch (err) { }

  return null;
};

const GroupLogos = ({ candidate }) => {
  const navigate = useNavigate();
  const groups = Array.isArray(candidate?.groups) ? candidate.groups : [];
  if (groups.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {groups.map((group) => (
        <HoverTip key={group.id} title={group.name}>
          <button
            type="button"
            aria-label={group.name}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/candidate-groups/${group.id}`);
            }}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sm leading-none hover:bg-sky-100"
          >
            {group.logo || '•'}
          </button>
        </HoverTip>
      ))}
    </span>
  );
};

// ==================== MEMOIZED DESKTOP TABLE ROW ====================
const InterviewRow = React.memo(({
  candidate,
  rowId,
  isSelected,
  onToggleSelect,
  isCompanyAdmin,
  statusConfig,
  languageDisplay,
  formatDate,
  handleCandidateClick,
  handleOpenFeedbackPage,
  onOpenJDDetails,
  onViewResult,
  onOpenTimeline,
  onOpenComments,
  onArchive,
  onViewFeedback,
  onLocalTagUpdate,
  t
}) => {
  const StatusIcon = statusConfig.icon;
  const fullName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || candidate.username || '—';
  const { display, tooltip } = truncateWithTooltip(fullName, 30);

  const statusTitle = (() => {
    if (candidate.status === 'completed') {
      const completedDate = candidate.ended_at || candidate.completed_at || candidate.started_at;
      if (completedDate) {
        return formatDate(completedDate);
      }
      return statusConfig?.label || t('interviewOverview.completed', 'Completed');
    }

    const parts = [statusConfig?.label];
    if (candidate.started_at) {
      parts.push(formatDate(candidate.started_at));
    }
    if (candidate.status === 'pending' && candidate.invite_expires_at) {
      parts.push(`${t('interviewOverview.expires')} ${formatDate(candidate.invite_expires_at)}`);
    }
    return parts.filter(Boolean).join(' · ');
  })();

  const languageTitle = (() => {
    const label = languageDisplay || '—';
    const native = candidate.language_detail?.native_name;
    if (native && label !== '—' && !String(label).includes(native)) {
      return `${label} · ${native}`;
    }
    return label;
  })();

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onToggleSelect(rowId);
  };

  const handleTagUpdate = (newTags) => {
    if (onLocalTagUpdate) {
      onLocalTagUpdate(candidate.session_id, newTags);
    }
  };

  return (
    <tr
      className={`transition-colors duration-75 ${
        isSelected
          ? 'bg-blue-50/70 hover:bg-blue-100/70'
          : 'odd:bg-white even:bg-gray-50 hover:bg-gray-100'
      }`}
    >
      {/* Checkbox */}
      <td className="py-2.5 px-3 text-center align-middle w-10 min-w-[42px]">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1 cursor-pointer"
          aria-label={`Select ${candidate.first_name || ''} ${candidate.last_name || ''}`}
        />
      </td>

      {/* Candidate */}
      <td className="py-2.5 px-4 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <HoverTip title={tooltip}>
                <button
                  onClick={() => handleCandidateClick(candidate.candidate_id)}
                  className="text-sm font-semibold text-gray-900 hover:text-blue-600 truncate transition-colors"
                >
                  {display}
                </button>
              </HoverTip>
              <GroupLogos candidate={candidate} />
              {languageDisplay !== '—' && (
                <HoverTip title={languageTitle}>
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 flex-shrink-0 rounded bg-violet-50 text-violet-600"
                    aria-label={languageTitle}
                  >
                    <Languages size={11} />
                  </span>
                </HoverTip>
              )}
              <HoverTip title={statusTitle}>
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 flex-shrink-0 rounded ${statusConfig.bgColor}`}
                  aria-label={statusConfig.label}
                >
                  <StatusIcon size={11} className={statusConfig.iconColor} />
                </span>
              </HoverTip>
              {candidate.resume_url && (
                <HoverTip
                  title={candidate.resume_uploaded_at
                    ? `${t('interviewOverview.viewResume', 'View resume')} · ${formatDate(candidate.resume_uploaded_at)}`
                    : t('interviewOverview.viewResume', 'View resume')}
                >
                  <a
                    href={candidate.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                    aria-label={t('interviewOverview.viewResume', 'View resume')}
                  >
                    <FileText size={14} />
                  </a>
                </HoverTip>
              )}
              {candidate.is_archived && (
                <span className="px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200 flex items-center gap-1 text-[10px] font-medium flex-shrink-0">
                  <Archive size={10} />
                  {t('interviewOverview.archived')}
                </span>
              )}
            </div>
            {candidate.status === 'completed' && (
              <>
                {isCompanyAdmin ? (
                  <div className="min-w-0 mt-0.5 relative z-50">
                    <TagEditor
                      tags={candidate.tags || []}
                      sessionId={candidate.session_id}
                      isCompanyAdmin
                      onTagsUpdate={handleTagUpdate}
                      maxTags={5}
                    />
                  </div>
                ) : (
                  (candidate.tags && candidate.tags.length > 0) ? (
                    <div className="flex items-center gap-1 overflow-hidden min-w-0 flex-wrap mt-0.5" role="group">
                      {candidate.tags.slice(0, 2).map((tag, tIdx) => {
                        const color = getTagColor(tag);
                        return (
                          <HoverTip key={tIdx} title={tag}>
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${color.bg} ${color.text} border ${color.border} truncate`}
                            >
                              <Tag size={8} />
                              {tag}
                            </span>
                          </HoverTip>
                        );
                      })}
                      {candidate.tags.length > 2 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">
                          +{candidate.tags.length - 2}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-400 text-[10px] mt-0.5">
                      <Tag size={10} />
                      <span>{t('interviewOverview.noTags')}</span>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </td>

      {/* Position */}
      <td className="py-2.5 px-4 align-middle">
        <div className="min-w-0">
          {(() => {
            const { display, tooltip } = truncateWithTooltip(candidate.job_title, 50);
            return (
              <button
                onClick={() => onOpenJDDetails && onOpenJDDetails(candidate.job_description_id)}
                className="flex items-center gap-1.5 group text-left w-full"
                title={tooltip}
              >
                <Briefcase size={14} className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                <span className="text-sm text-gray-900 group-hover:text-blue-600 whitespace-nowrap transition-colors">
                  {display || '—'}
                </span>
              </button>
            );
          })()}
          {candidate.experience_level && (
            <div className="text-[10px] text-gray-500 mt-0.5 truncate" title={candidate.experience_level}>
              {candidate.experience_level}
            </div>
          )}
        </div>
      </td>

      {/* Scheduled By */}
      <td className="py-2.5 px-4 align-middle">
        <CompanyAdminName name={candidate.scheduled_by_name} />
      </td>

      {/* Score / Total */}
      <td className="py-2.5 px-4 align-middle">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className={`flex-shrink-0 ${candidate.effective_score >= 80 ? 'text-emerald-500' : candidate.effective_score >= 60 ? 'text-sky-500' : candidate.effective_score >= 40 ? 'text-orange-500' : 'text-red-500'}`} />
          <div className="flex items-baseline gap-1.5">
            <span
              className={`text-sm font-bold ${candidate.effective_score >= 80 ? 'text-emerald-600' : candidate.effective_score >= 60 ? 'text-sky-600' : candidate.effective_score >= 40 ? 'text-orange-600' : 'text-red-600'}`}
              title={`${t('interviewOverview.effective')}: ${formatScore(candidate.effective_score)}`}
            >
              {formatScore(candidate.effective_score)}
            </span>
            <span className="text-xs text-gray-400 font-normal">/</span>
            <span
              className="text-sm font-semibold text-gray-600"
              title={`${t('interviewOverview.total')}: ${formatScore(candidate.total_score)}`}
            >
              {formatScore(candidate.total_score)}
            </span>
          </div>
        </div>
      </td>

      {/* Actions */}
      <td className="py-2.5 px-1 align-middle text-center w-auto">
        <div className="flex items-center justify-center gap-1">
          {candidate.status === 'completed' && (
            <>
              <button
                onClick={() => onViewResult(candidate)}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title={t('interviewOverview.viewResults')}
                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Eye size={14} />
              </button>
              {onOpenTimeline && (
                <button
                  onClick={() => onOpenTimeline(candidate.session_id, {
                    candidateName: `${candidate.first_name} ${candidate.last_name}`,
                    sessionId: candidate.session_id
                  })}
                  className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                  title={t('interviewOverview.viewTimeline')}
                  style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Clock size={14} />
                </button>
              )}
              <button
                onClick={() => handleOpenFeedbackPage(candidate)}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title={t('interviewOverview.openFeedbackPage', 'Open feedback page in new tab')}
                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ExternalLink size={14} />
              </button>
            </>
          )}
          {/* Post-Interview Feedback button - only for completed interviews */}
          {candidate.status === 'completed' && onViewFeedback && (
            <button
              onClick={() => onViewFeedback(candidate.session_id, candidate)}
              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
              title={t('interviewOverview.viewFeedback', 'Post-Interview Feedback')}
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ClipboardList size={14} />
            </button>
          )}
          {/* Comments button - available for all sessions */}
          {candidate.session_id && (
            <button
              onClick={() => {
                if (onOpenComments) {
                  onOpenComments(candidate.session_id, candidate);
                }
              }}
              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
              title={t('comments.title', 'Comments')}
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <MessageSquare size={14} />
            </button>
          )}
          {/* Archive/Unarchive button - only for completed interviews */}
          {candidate.status === 'completed' && onArchive && (
            <button
              onClick={() => onArchive(candidate)}
              className={`p-1.5 rounded-full transition-colors ${candidate.is_archived
                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                : 'text-orange-600 hover:bg-orange-50'
                }`}
              title={candidate.is_archived
                ? t('interviewOverview.unarchiveInterview', 'Unarchive interview')
                : t('interviewOverview.archiveInterview', 'Archive interview')
              }
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Archive size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
});

// ==================== MEMOIZED MOBILE CARD ====================
const InterviewCard = React.memo(({
  candidate,
  rowId,
  isSelected,
  onToggleSelect,
  isCompanyAdmin,
  statusConfig,
  languageDisplay,
  formatDate,
  handleCandidateClick,
  handleOpenFeedbackPage,
  onOpenJDDetails,
  onViewResult,
  onOpenTimeline,
  onOpenComments,
  onArchive,
  onViewFeedback,
  onLocalTagUpdate,
  t
}) => {
  const StatusIcon = statusConfig.icon;
  const fullName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || candidate.username || '—';
  const { display, tooltip } = truncateWithTooltip(fullName, 30);

  const statusTitle = (() => {
    if (candidate.status === 'completed') {
      const completedDate = candidate.ended_at || candidate.completed_at || candidate.started_at;
      if (completedDate) {
        return formatDate(completedDate);
      }
      return statusConfig?.label || t('interviewOverview.completed', 'Completed');
    }

    const parts = [statusConfig?.label];
    if (candidate.started_at) {
      parts.push(formatDate(candidate.started_at));
    }
    if (candidate.status === 'pending' && candidate.invite_expires_at) {
      parts.push(`${t('interviewOverview.expires')} ${formatDate(candidate.invite_expires_at)}`);
    }
    return parts.filter(Boolean).join(' · ');
  })();

  const languageTitle = (() => {
    const label = languageDisplay || '—';
    const native = candidate.language_detail?.native_name;
    if (native && label !== '—' && !String(label).includes(native)) {
      return `${label} · ${native}`;
    }
    return label;
  })();

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onToggleSelect(rowId);
  };

  const handleTagUpdate = (newTags) => {
    if (onLocalTagUpdate) {
      onLocalTagUpdate(candidate.session_id, newTags);
    }
  };

  return (
    <article
      className={`border rounded-lg p-4 transition-colors duration-75 ${
        isSelected
          ? 'border-blue-300 bg-blue-50/40 shadow-sm'
          : 'border-gray-200 bg-white shadow-sm hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1 cursor-pointer flex-shrink-0 mt-0.5"
            aria-label={`Select ${candidate.first_name || ''} ${candidate.last_name || ''}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <HoverTip title={tooltip}>
                <button
                  onClick={() => handleCandidateClick(candidate.candidate_id)}
                  className="text-base font-semibold text-gray-900 hover:text-blue-600 truncate transition-colors"
                >
                  {display}
                </button>
              </HoverTip>
              <GroupLogos candidate={candidate} />
              {languageDisplay !== '—' && (
                <HoverTip title={languageTitle}>
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 flex-shrink-0 rounded bg-violet-50 text-violet-600"
                    aria-label={languageTitle}
                  >
                    <Languages size={11} />
                  </span>
                </HoverTip>
              )}
              <HoverTip title={statusTitle}>
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 flex-shrink-0 rounded ${statusConfig.bgColor}`}
                  aria-label={statusConfig.label}
                >
                  <StatusIcon size={11} className={statusConfig.iconColor} />
                </span>
              </HoverTip>
              {candidate.resume_url && (
                <HoverTip
                  title={candidate.resume_uploaded_at
                    ? `${t('interviewOverview.viewResume', 'View resume')} · ${formatDate(candidate.resume_uploaded_at)}`
                    : t('interviewOverview.viewResume', 'View resume')}
                >
                  <a
                    href={candidate.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                    aria-label={t('interviewOverview.viewResume', 'View resume')}
                  >
                    <FileText size={14} />
                  </a>
                </HoverTip>
              )}
              {candidate.is_archived && (
                <span className="px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200 flex items-center gap-1 text-[10px] font-medium flex-shrink-0">
                  <Archive size={10} />
                  {t('interviewOverview.archived')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-start gap-2">
          <Briefcase size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500 mb-0.5">{t('interviewOverview.position')}</div>
            {(() => {
              const { display, tooltip } = truncateWithTooltip(candidate.job_title, 50);
              return (
                <button
                  onClick={() => onOpenJDDetails && onOpenJDDetails(candidate.job_description_id)}
                  className="text-sm text-gray-900 hover:text-blue-600 block text-left transition-colors"
                  title={tooltip}
                >
                  {display || '—'}
                </button>
              );
            })()}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <UserRound size={16} className="text-indigo-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500 mb-0.5">{t('interviewOverview.companyAdmin', 'Company Admin')}</div>
            <CompanyAdminName name={candidate.scheduled_by_name} />
          </div>
        </div>
        <div className="flex items-start gap-2">
          <TrendingUp size={16} className={`mt-0.5 flex-shrink-0 ${candidate.effective_score >= 80 ? 'text-emerald-500' : candidate.effective_score >= 60 ? 'text-sky-500' : candidate.effective_score >= 40 ? 'text-orange-500' : 'text-red-500'}`} />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500 mb-0.5">{t('interviewOverview.scoreTotal')}</div>
            <div className="flex items-baseline gap-1.5">
              <span
                className={`text-sm font-bold ${candidate.effective_score >= 80 ? 'text-emerald-600' : candidate.effective_score >= 60 ? 'text-sky-600' : candidate.effective_score >= 40 ? 'text-orange-600' : 'text-red-600'}`}
                title={`${t('interviewOverview.effective')}: ${formatScore(candidate.effective_score)}`}
              >
                {formatScore(candidate.effective_score)}
              </span>
              <span className="text-xs text-gray-400 font-normal">/</span>
              <span
                className="text-sm font-semibold text-gray-600"
                title={`${t('interviewOverview.total')}: ${formatScore(candidate.total_score)}`}
              >
                {formatScore(candidate.total_score)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2 overflow-hidden flex-1">
          {candidate.status === 'completed' && (
            <>
              {isCompanyAdmin ? (
                <div className="min-w-0 relative z-50">
                  <TagEditor
                    tags={candidate.tags || []}
                    sessionId={candidate.session_id}
                    isCompanyAdmin
                    onTagsUpdate={handleTagUpdate}
                    maxTags={5}
                  />
                </div>
              ) : (
                (candidate.tags && candidate.tags.length > 0) ? (
                  <div className="flex gap-1 overflow-x-auto">
                    {candidate.tags.map((tag, i) => {
                      const color = getTagColor(tag);
                      return (
                        <HoverTip key={i} title={tag}>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${color.bg} ${color.text} border ${color.border}`} title={tag}>
                            <Tag size={8} />
                            {tag}
                          </span>
                        </HoverTip>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-gray-400 text-[10px]">
                    <Tag size={10} />
                    <span>{t('interviewOverview.noTags')}</span>
                  </div>
                )
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {candidate.status === 'completed' && (
            <>
              <button
                onClick={() => onViewResult(candidate)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title={t('interviewOverview.viewResults')}
                style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Eye size={16} />
              </button>
              {onOpenTimeline && (
                <button
                  onClick={() => onOpenTimeline(candidate.session_id, {
                    candidateName: `${candidate.first_name} ${candidate.last_name}`,
                    sessionId: candidate.session_id
                  })}
                  className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                  title={t('interviewOverview.viewTimeline')}
                  style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Clock size={16} />
                </button>
              )}
              <button
                onClick={() => handleOpenFeedbackPage(candidate)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title={t('interviewOverview.openFeedbackPage', 'Open feedback page in new tab')}
                style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ExternalLink size={16} />
              </button>
            </>
          )}
          {/* Post-Interview Feedback button - only for completed interviews */}
          {candidate.status === 'completed' && onViewFeedback && (
            <button
              onClick={() => onViewFeedback(candidate.session_id, candidate)}
              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
              title={t('interviewOverview.viewFeedback', 'Post-Interview Feedback')}
              style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ClipboardList size={16} />
            </button>
          )}
          {/* Comments button - available for all sessions */}
          {candidate.session_id && (
            <button
              onClick={() => {
                if (onOpenComments) {
                  onOpenComments(candidate.session_id, candidate);
                }
              }}
              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
              title={t('comments.title', 'Comments')}
              style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <MessageSquare size={16} />
            </button>
          )}
          {/* Archive/Unarchive button - only for completed interviews */}
          {candidate.status === 'completed' && onArchive && (
            <button
              onClick={() => onArchive(candidate)}
              className={`p-2 rounded-full transition-colors ${candidate.is_archived
                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                : 'text-orange-600 hover:bg-orange-50'
                }`}
              title={candidate.is_archived
                ? t('interviewOverview.unarchiveInterview', 'Unarchive interview')
                : t('interviewOverview.archiveInterview', 'Archive interview')
              }
              style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Archive size={14} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
});

// ==================== MAIN INTERVIEW TABLE ====================
const InterviewTable = ({
  candidates = [],
  onViewResult,
  formatDate = (d) => d,
  onSort = () => { },
  getSortDirection = () => null,
  isCompanyAdmin = false,
  onTagsUpdated,
  onOpenJDDetails,
  currentPage = 1,
  companies = [],
  onOpenTimeline,
  onOpenComments,
  onArchive,
  onViewFeedback,
  selectedSessionIds = [],
  selectedSet = null,
  onToggleSelect = () => { },
  onSelectAll = () => { },
  isSeeAll = false,
  onLoadMore = null,
  hasMore = false,
  isLoadingMore = false,
  scrollContainerRef = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = React.useState(candidates);
  const selectAllRef = React.useRef(null);
  const mobileSelectAllRef = React.useRef(null);

  const getRowId = React.useCallback((candidate) => {
    return getInterviewRowId(candidate);
  }, []);

  const {
    isVirtual,
    visibleItems: visibleRows,
    topPadding,
    bottomPadding
  } = useTableVirtualizer({
    enabled: isSeeAll,
    items: rows,
    itemHeight: 56,
    scrollContainerRef,
    overscan: 8,
    onLoadMore,
    hasMore,
    isLoadingMore
  });

  const currentPageIds = React.useMemo(() => {
    return (rows || [])
      .map(c => getRowId(c))
      .filter(Boolean);
  }, [rows, getRowId]);

  const effectiveSelectedSet = React.useMemo(() => {
    return selectedSet || new Set((selectedSessionIds || []).map(String));
  }, [selectedSet, selectedSessionIds]);

  const currentPageSelectedCount = React.useMemo(() => {
    return currentPageIds.filter(id => effectiveSelectedSet.has(String(id))).length;
  }, [currentPageIds, effectiveSelectedSet]);

  const isAllSelected = currentPageIds.length > 0 && currentPageSelectedCount === currentPageIds.length;
  const isIndeterminate = currentPageSelectedCount > 0 && currentPageSelectedCount < currentPageIds.length;

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
    if (mobileSelectAllRef.current) {
      mobileSelectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const companyLanguageMap = React.useMemo(() => {
    const map = {};
    (companies || []).forEach((company) => {
      if (company?.id && company.default_language_detail) {
        map[company.id] = company.default_language_detail;
      }
    });
    return map;
  }, [companies]);

  React.useEffect(() => {
    setRows(candidates || []);
  }, [candidates]);

  const getStatusConfig = React.useCallback((status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800', iconColor: 'text-green-600', label: t('interviewOverview.completed') };
      case 'in_progress':
        return { icon: Clock, bgColor: 'bg-blue-100', textColor: 'text-blue-800', iconColor: 'text-blue-600', label: t('interviewOverview.inProgress') };
      case 'pending':
        return { icon: Calendar, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', iconColor: 'text-yellow-600', label: t('interviewOverview.scheduled') };
      case 'expired':
        return { icon: XCircle, bgColor: 'bg-red-100', textColor: 'text-red-800', iconColor: 'text-red-600', label: t('interviewOverview.expired') };
      default:
        return { icon: AlertCircle, bgColor: 'bg-gray-100', textColor: 'text-gray-800', iconColor: 'text-gray-600', label: status || t('interviewOverview.unknown') };
    }
  }, [t]);

  const getLanguageDisplay = React.useCallback((candidate = {}) => {
    const candidateLang =
      candidate.language_detail ||
      (typeof candidate.language === 'object' ? candidate.language : null);
    const companyLang = candidate.company_id ? companyLanguageMap[candidate.company_id] : null;
    const detail = candidateLang || companyLang;

    const name = detail?.name || detail?.native_name || candidate.language_name;
    const code =
      detail?.code ||
      candidate.language_code ||
      (typeof candidate.language === 'string' ? candidate.language : null);

    if (name && code && name.toLowerCase() !== String(code).toLowerCase()) {
      return `${name} (${String(code).toUpperCase()})`;
    }
    if (name) return name;
    if (code) return String(code).toUpperCase();
    return '—';
  }, [companyLanguageMap]);

  const handleCandidateClick = React.useCallback((candidateId) => {
    try {
      localStorage.setItem('interviewOverview_page', String(currentPage || 1));
      localStorage.setItem('interviewOverview_fromDetails', 'true');
    } catch (e) { }
    navigate(`/candidatedetails/${candidateId}`);
  }, [currentPage, navigate]);

  const handleOpenFeedbackPage = React.useCallback((candidate) => {
    const companyId = getCompanyId(candidate);
    const candidateId = candidate.candidate_id;
    const interviewId = candidate.session_id;

    if (!companyId || !candidateId || !interviewId) {
      console.error('Missing required IDs for feedback page');
      return;
    }
    const url = `/feedbacks/${companyId}/${candidateId}/${interviewId}`;
    window.open(url, '_blank');
  }, []);

  const handleLocalTagUpdate = React.useCallback((sessionId, newTags) => {
    setRows(prev => prev.map(r => (r.session_id === sessionId ? { ...r, tags: newTags } : r)));
    if (typeof onTagsUpdated === 'function') {
      onTagsUpdated(newTags, sessionId);
    }
  }, [onTagsUpdated]);

  return (
    <div className="w-full">
      {/* ---------- DESKTOP / TABLE ---------- */}
      <div className="hidden md:block rounded-lg border border-gray-200 w-full overflow-hidden shadow-sm">
        <div className="overflow-x-auto overflow-y-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="w-full table-auto bg-white divide-y divide-gray-200" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="py-2 px-3 text-center w-10 min-w-[42px] select-none align-middle">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={selectAllRef}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1 cursor-pointer"
                    aria-label={t('interviewOverview.selectAll', 'Select all on this page')}
                  />
                </th>

                <SortableHeader
                  sortKey="candidate__first_name"
                  currentSortKey={getSortDirection('candidate__first_name') ? 'candidate__first_name' : null}
                  sortDirection={getSortDirection('candidate__first_name')}
                  onSort={onSort}
                  label={t('interviewOverview.candidate')}
                >
                  <div className="flex items-center gap-2">
                    <span>{t('interviewOverview.candidate')}</span>
                  </div>
                </SortableHeader>

                <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider align-middle">
                  <div className="flex items-center gap-2">
                    <Briefcase size={12} className="text-gray-400" />
                    <span>{t('interviewOverview.position')}</span>
                  </div>
                </th>

                <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider align-middle">
                  <div className="flex items-center gap-2">
                    <UserRound size={12} className="text-indigo-500" />
                    <span>{t('interviewOverview.companyAdmin', 'Company Admin')}</span>
                  </div>
                </th>

                <SortableHeader
                  sortKey="effective_score"
                  currentSortKey={getSortDirection('effective_score') ? 'effective_score' : null}
                  sortDirection={getSortDirection('effective_score')}
                  onSort={onSort}
                  label={t('interviewOverview.scoreTotal')}
                >
                  <div className="flex items-center gap-2">
                    <TrendingUp size={12} className="text-gray-400" />
                    <span>{t('interviewOverview.scoreTotal')}</span>
                  </div>
                </SortableHeader>

                <th className="text-center py-2 px-1 text-xs font-semibold text-gray-500 uppercase tracking-wider w-auto align-middle">
                  <span>{t('interviewOverview.actions')}</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {isVirtual && topPadding > 0 && (
                <tr style={{ height: `${topPadding}px` }} aria-hidden="true">
                  <td colSpan={6} style={{ padding: 0, border: 'none', height: `${topPadding}px` }} />
                </tr>
              )}
              {visibleRows.map((candidate) => {
                const rowId = getRowId(candidate);
                const isSelected = effectiveSelectedSet.has(String(rowId));
                const statusConfig = getStatusConfig(candidate.status);
                const languageDisplay = getLanguageDisplay(candidate);

                return (
                  <InterviewRow
                    key={rowId}
                    candidate={candidate}
                    rowId={rowId}
                    isSelected={isSelected}
                    onToggleSelect={onToggleSelect}
                    isCompanyAdmin={isCompanyAdmin}
                    statusConfig={statusConfig}
                    languageDisplay={languageDisplay}
                    formatDate={formatDate}
                    handleCandidateClick={handleCandidateClick}
                    handleOpenFeedbackPage={handleOpenFeedbackPage}
                    onOpenJDDetails={onOpenJDDetails}
                    onViewResult={onViewResult}
                    onOpenTimeline={onOpenTimeline}
                    onOpenComments={onOpenComments}
                    onArchive={onArchive}
                    onViewFeedback={onViewFeedback}
                    onLocalTagUpdate={handleLocalTagUpdate}
                    t={t}
                  />
                );
              })}
              {isVirtual && bottomPadding > 0 && (
                <tr style={{ height: `${bottomPadding}px` }} aria-hidden="true">
                  <td colSpan={6} style={{ padding: 0, border: 'none', height: `${bottomPadding}px` }} />
                </tr>
              )}
            </tbody>
          </table>
          {isSeeAll && isLoadingMore && (
            <div className="flex items-center justify-center py-3 text-sm text-blue-600 gap-2 bg-blue-50/50 border-t border-blue-100">
              <CircularProgress size={16} sx={{ color: '#2563eb' }} />
              <span>{t('interviewOverview.loadingMore', 'Loading more candidates...')}</span>
            </div>
          )}
        </div>
      </div>

      {/* ---------- MOBILE / CARDS ---------- */}
      <div className="md:hidden space-y-3">
        {rows.length > 0 && (
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-700">
            <label className="flex items-center gap-2 cursor-pointer font-medium select-none">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={mobileSelectAllRef}
                onChange={(e) => onSelectAll(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1 cursor-pointer"
              />
              <span>{t('interviewOverview.selectAll', 'Select all on this page')}</span>
            </label>
            {currentPageSelectedCount > 0 && (
              <span className="font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                {t('interviewOverview.selectedCount', { count: currentPageSelectedCount, defaultValue: `${currentPageSelectedCount} selected` })}
              </span>
            )}
          </div>
        )}
        {isVirtual && topPadding > 0 && (
          <div style={{ height: `${topPadding}px` }} aria-hidden="true" />
        )}
        {visibleRows.map((candidate) => {
          const rowId = getRowId(candidate);
          const isSelected = effectiveSelectedSet.has(String(rowId));
          const statusConfig = getStatusConfig(candidate.status);
          const languageDisplay = getLanguageDisplay(candidate);

          return (
            <InterviewCard
              key={rowId}
              candidate={candidate}
              rowId={rowId}
              isSelected={isSelected}
              onToggleSelect={onToggleSelect}
              isCompanyAdmin={isCompanyAdmin}
              statusConfig={statusConfig}
              languageDisplay={languageDisplay}
              formatDate={formatDate}
              handleCandidateClick={handleCandidateClick}
              handleOpenFeedbackPage={handleOpenFeedbackPage}
              onOpenJDDetails={onOpenJDDetails}
              onViewResult={onViewResult}
              onOpenTimeline={onOpenTimeline}
              onOpenComments={onOpenComments}
              onArchive={onArchive}
              onViewFeedback={onViewFeedback}
              onLocalTagUpdate={handleLocalTagUpdate}
              t={t}
            />
          );
        })}
        {isVirtual && bottomPadding > 0 && (
          <div style={{ height: `${bottomPadding}px` }} aria-hidden="true" />
        )}
        {isSeeAll && isLoadingMore && (
          <div className="flex items-center justify-center py-3 text-sm text-blue-600 gap-2 bg-blue-50/50 rounded-lg">
            <CircularProgress size={16} sx={{ color: '#2563eb' }} />
            <span>{t('interviewOverview.loadingMore', 'Loading more candidates...')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(InterviewTable);
